import React from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  InputNumber,
  Row,
  Col,
  Alert,
  Typography,
} from 'antd';
import SmartFormField from './SmartFormField';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

interface TaskContainerProps {
  step: string;
  task: string | null;
  projectCode: string;
  onTaskChange: (taskId: string) => void;
  onAutoSave: () => void;
}

const TaskContainer: React.FC<TaskContainerProps> = ({
  step,
  task,
  projectCode,
  onTaskChange,
  onAutoSave,
}) => {
  const [form] = Form.useForm();

  // 如果没有选择任务，显示任务选择提示
  if (!task) {
    return (
      <Alert
        message="请选择要执行的具体任务"
        description="从上方任务进度条或左侧导航中选择具体任务开始操作"
        type="info"
        showIcon
      />
    );
  }

  // 渲染不同任务的内容
  const renderTaskContent = () => {
    switch (task) {
      case 'basic_info':
        return (
          <Card title="基本信息收集" style={{ marginBottom: '24px' }}>
            <Form form={form} layout="vertical" onValuesChange={onAutoSave}>
              <Row gutter={[24, 16]}>
                <Col span={12}>
                  <SmartFormField
                    name="companyNameCN"
                    label="公司法定全称（中文）"
                    type="input"
                    required={true}
                    placeholder="请输入公司中文全称"
                    helpText="请填写工商注册的完整公司名称"
                  />
                </Col>
                <Col span={12}>
                  <SmartFormField
                    name="companyNameEN"
                    label="公司法定全称（英文）"
                    type="input"
                    required={true}
                    placeholder="请输入公司英文全称"
                    helpText="如有英文名称，请准确填写"
                  />
                </Col>
                <Col span={12}>
                  <SmartFormField
                    name="registrationNumber"
                    label="统一社会信用代码"
                    type="input"
                    required={true}
                    placeholder="请输入统一社会信用代码"
                    helpText="18位统一社会信用代码"
                    validation={{
                      pattern:
                        /^[0-9A-HJ-NPQRTUWXY]{2}\d{6}[0-9A-HJ-NPQRTUWXY]{10}$/,
                      message: '请输入正确的统一社会信用代码',
                    }}
                  />
                </Col>
                <Col span={12}>
                  <SmartFormField
                    name="businessScope"
                    label="主营业务范围"
                    type="textarea"
                    required={true}
                    placeholder="请简要描述主营业务范围"
                    helpText="请描述公司主要经营范围和业务领域"
                  />
                </Col>
              </Row>
            </Form>
          </Card>
        );

      case 'equity_structure':
        return (
          <Card title="股权结构分析" style={{ marginBottom: '24px' }}>
            <Form form={form} layout="vertical" onValuesChange={onAutoSave}>
              <Row gutter={[24, 16]}>
                <Col span={12}>
                  <SmartFormField
                    name="registeredCapital"
                    label="注册资本（万元）"
                    type="number"
                    required={true}
                    placeholder="请输入注册资本"
                    helpText="以万元为单位"
                    min={0}
                  />
                </Col>
                <Col span={12}>
                  <SmartFormField
                    name="paidCapital"
                    label="实缴资本（万元）"
                    type="number"
                    required={true}
                    placeholder="请输入实缴资本"
                    helpText="实际缴纳的资本金额"
                    min={0}
                  />
                </Col>
                <Col span={12}>
                  <SmartFormField
                    name="controllingParty"
                    label="控股股东"
                    type="input"
                    required={true}
                    placeholder="请输入控股股东名称"
                    helpText="持股比例最高的股东"
                  />
                </Col>
                <Col span={12}>
                  <SmartFormField
                    name="shareholderRatio"
                    label="控股股东持股比例（%）"
                    type="number"
                    required={true}
                    placeholder="请输入持股比例"
                    helpText="控股股东的持股比例"
                    min={0}
                    max={100}
                  />
                </Col>
                <Col span={24}>
                  <SmartFormField
                    name="equityStructure"
                    label="股权结构说明"
                    type="textarea"
                    required={true}
                    placeholder="请详细描述股权结构情况"
                    helpText="包括主要股东、持股比例、股权关系等"
                    rows={4}
                  />
                </Col>
              </Row>
            </Form>
          </Card>
        );

      case 'governance_structure':
        return (
          <Card title="治理架构评估" style={{ marginBottom: '24px' }}>
            <Form form={form} layout="vertical" onValuesChange={onAutoSave}>
              <Row gutter={[24, 16]}>
                <Col span={12}>
                  <SmartFormField
                    name="boardSize"
                    label="董事会规模"
                    type="number"
                    required={true}
                    placeholder="请输入董事会人数"
                    helpText="董事会总人数"
                    min={3}
                    max={19}
                  />
                </Col>
                <Col span={12}>
                  <SmartFormField
                    name="independentDirectors"
                    label="独立董事人数"
                    type="number"
                    required={true}
                    placeholder="请输入独立董事人数"
                    helpText="独立董事数量"
                    min={0}
                  />
                </Col>
                <Col span={12}>
                  <SmartFormField
                    name="chairmanType"
                    label="董事长性质"
                    type="select"
                    required={true}
                    placeholder="请选择董事长性质"
                    helpText="董事长是否为执行董事"
                    options={[
                      { label: '执行董事长', value: 'executive' },
                      { label: '非执行董事长', value: 'non-executive' },
                      { label: '独立董事长', value: 'independent' },
                    ]}
                  />
                </Col>
                <Col span={12}>
                  <SmartFormField
                    name="ceoType"
                    label="总经理任职情况"
                    type="select"
                    required={true}
                    placeholder="请选择总经理任职情况"
                    helpText="总经理是否兼任董事长"
                    options={[
                      { label: '董事长兼总经理', value: 'chairman-ceo' },
                      { label: '独立总经理', value: 'independent-ceo' },
                      { label: '董事兼总经理', value: 'director-ceo' },
                    ]}
                  />
                </Col>
              </Row>
            </Form>
          </Card>
        );

      case 'related_parties':
        return (
          <Card title="关联关系梳理" style={{ marginBottom: '24px' }}>
            <Form form={form} layout="vertical" onValuesChange={onAutoSave}>
              <Row gutter={[24, 16]}>
                <Col span={12}>
                  <SmartFormField
                    name="parentCompany"
                    label="母公司名称"
                    type="input"
                    placeholder="如有母公司，请输入名称"
                    helpText="如为集团子公司，请填写母公司信息"
                  />
                </Col>
                <Col span={12}>
                  <SmartFormField
                    name="subsidiaryCount"
                    label="子公司数量"
                    type="number"
                    placeholder="请输入子公司数量"
                    helpText="包括全资和控股子公司"
                    min={0}
                  />
                </Col>
                <Col span={24}>
                  <SmartFormField
                    name="majorRelatedParties"
                    label="主要关联方"
                    type="textarea"
                    placeholder="请列出主要关联方及关联关系"
                    helpText="包括关联企业、关联个人等"
                    rows={4}
                  />
                </Col>
                <Col span={24}>
                  <SmartFormField
                    name="relatedTransactions"
                    label="重大关联交易"
                    type="textarea"
                    placeholder="请描述近两年重大关联交易情况"
                    helpText="金额、性质、必要性等"
                    rows={4}
                  />
                </Col>
              </Row>
            </Form>
          </Card>
        );

      default:
        return (
          <Card>
            <Alert
              message="暂无内容"
              description={`任务 "${task}" 暂无可用内容`}
              type="info"
              showIcon
            />
          </Card>
        );
    }
  };

  return (
    <div>
      {renderTaskContent()}

      {/* 智能提示区域 */}
      <Card
        size="small"
        style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            💡 智能提示:
          </Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            表单数据将自动保存，您可以随时切换任务或步骤
          </Text>
        </div>
      </Card>
    </div>
  );
};

export default TaskContainer;
